import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchSongs, deleteSong } from "../../services/songService";
import { fetchCollections } from "../../services/songCollectionsService";
import { useAuth } from "../../contexts/AuthContext";

// Create context
const SongContext = createContext();

// Export custom hook for using this context
export const useSongs = () => useContext(SongContext);

const SongStateProvider = ({ children, onCountsUpdate }) => {
  const { user, isAdminOfCommunity, userCommunities, activeCommunity } = useAuth();

  // State for songs
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state - default to user's primary community if available
  const getInitialOwner = () => {
    if (activeCommunity?.id) {
      return `community-${activeCommunity.id}`;
    }
    return "user";
  };
  const [selectedOwner, setSelectedOwner] = useState(getInitialOwner); // 'user' or 'community-{communityId}'
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Vse");
  const [sortField, setSortField] = useState("title");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showSongModal, setShowSongModal] = useState(false);
  const [currentSong, setCurrentSong] = useState(null);
  
  // Collections state
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [currentSongForCollection, setCurrentSongForCollection] = useState(null);
  
  // Extract unique categories from songs
  const [categories, setCategories] = useState([]);
  // Tag filter state
  const [tagFilter, setTagFilter] = useState("Vse");
  // Extract unique tags from songs
  const [availableTags, setAvailableTags] = useState([]);

  // Extract categories and tags whenever songs change
  useEffect(() => {
    if (songs && songs.length) {
      const uniqueCategories = [...new Set(songs.map(song => song.category).filter(Boolean))];
      setCategories(uniqueCategories);

      const tagSet = new Set();
      songs.forEach(song => {
        if (song.tags) {
          song.tags.forEach(tag => tagSet.add(tag.name));
        }
      });
      setAvailableTags([...tagSet].sort());
    }
  }, [songs]);
  
  // Refresh song list - use useCallback but with no deps to prevent infinite loops
  const refreshSongs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchSongs();
      setSongs(data);
      
      // Update counts if callback is provided
      if (onCountsUpdate) {
        const counts = {
          user: data.filter(song => song.owner_type === 'user').length,
          community: data.filter(song => song.owner_type === 'community').length,
          all: data.length
        };
        onCountsUpdate(counts);
      }
    } catch (err) {
      setError("Failed to load songs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Get song count by ownership type
  const getSongCountByOwnership = useCallback((ownershipType) => {
    if (!songs || !songs.length) return 0;
    
    switch (ownershipType) {
      case 'user':
        return songs.filter(song => song.owner_type === 'user').length;
      case 'community':
        return songs.filter(song => song.owner_type === 'community').length;
      case 'all':
      default:
        return songs.length;
    }
  }, [songs]);
  
  // Load collections - with empty dependency array
  const loadCollections = useCallback(async () => {
    try {
      const data = await fetchCollections();
      setCollections(data);
      
      // Only update collection counts once, don't trigger re-renders
      if (onCountsUpdate && data.length > 0) {
        const counts = {
          user: data.filter(c => c.owner_type === 'user').length,
          community: data.filter(c => c.owner_type === 'community').length,
          all: data.length
        };
      }
    } catch (err) {
    }
  }, []);
  
  // Initial data loading - with empty dependency array to prevent loops
  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      try {
        if (isMounted) {
          await refreshSongs();
          await loadCollections();
        }
      } catch (error) {
      }
    };
    
    loadInitialData();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Apply filters, search and sort
  useEffect(() => {
    let result = [...songs];
    
    // Filter by ownership selection
    if (selectedOwner === "user") {
      result = result.filter(song => song.owner_type === "user");
    } else if (selectedOwner.startsWith("community-")) {
      const communityId = parseInt(selectedOwner.split("-")[1]);
      result = result.filter(song => 
        song.owner_type === "community" && song.owner_id === communityId
      );
    }
    
    // Apply category filter
    if (categoryFilter && categoryFilter !== "Vse") {
      result = result.filter(song => song.category === categoryFilter);
    }

    // Apply tag filter
    if (tagFilter && tagFilter !== "Vse") {
      result = result.filter(song =>
        song.tags && song.tags.some(tag => tag.name === tagFilter)
      );
    }

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(song =>
        song.title?.toLowerCase().includes(term) ||
        song.composer?.toLowerCase().includes(term) ||
        song.category?.toLowerCase().includes(term) ||
        (song.tags && song.tags.some(tag => tag.name.toLowerCase().includes(term)))
      );
    }
    
    // Apply sort
    result.sort((a, b) => {
      const fieldA = a[sortField]?.toLowerCase() || '';
      const fieldB = b[sortField]?.toLowerCase() || '';
      
      if (sortDirection === 'asc') {
        return fieldA.localeCompare(fieldB);
      } else {
        return fieldB.localeCompare(fieldA);
      }
    });
    
    setFilteredSongs(result);
  }, [songs, selectedOwner, searchTerm, categoryFilter, tagFilter, sortField, sortDirection]);
  
  // Handle song deletion
  const handleDelete = useCallback(async (songId) => {
    if (!songId) return;
    
    if (!window.confirm('Are you sure you want to delete this song?')) {
      return;
    }
    
    setLoading(true);
    
    try {
      await deleteSong(songId);
      await refreshSongs(); 
      alert('Song deleted successfully');
    } catch (err) {
      alert('Failed to delete song. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [refreshSongs]);
  
  // Function to handle sorting
  const handleSort = useCallback((field) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending for new sort field
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);
  
  // Function to handle owner change (dropdown selection)
  const handleOwnerChange = useCallback((value) => {
    setSelectedOwner(value);
  }, []);
  
  // Function to check if user can edit a song
  const canEditSong = useCallback((song) => {
    if (!user) return false;
    
    // User's own song
    if (song.owner_type === 'user' && song.owner_id === user.id) {
      return true;
    }
    
    // Community song and user is admin
    if (song.owner_type === 'community' && isAdminOfCommunity(song.owner_id)) {
      return true;
    }
    
    // Global admin can edit any song
    return user?.role === 'global_admin';
  }, [user, isAdminOfCommunity]);
  
  // Function to open song modal for edit
  const handleOpenSongModal = useCallback((song = null) => {
    setCurrentSong(song);
    setShowSongModal(true);
  }, []);
  
  // Function to open collection modal
  const handleOpenCollectionModal = useCallback((song) => {
    setCurrentSongForCollection(song);
    // Reset selected collections
    setSelectedCollections([]);
    setShowCollectionModal(true);
  }, []);
  
  // Function to handle collection selection
  const handleCollectionSelect = useCallback((collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      } else {
        return [...prev, collectionId];
      }
    });
  }, []);
  
  // Function to handle adding to collections
  const handleAddToCollections = useCallback(async () => {
    // Implementation for adding to collections
    setShowCollectionModal(false);
  }, []);
  
  // Function to get category badge class
  const getCategoryBadgeClass = useCallback((category) => {
    const categoryMap = {
      "Euharistična": "bg-danger",
      "Marijanska": "bg-primary",
      "Adventna": "bg-info",
      "Postna": "bg-secondary",
      "Otroška": "bg-warning",
      "Narodna": "bg-success",
      "Krst": "bg-info",
      "Poroka": "bg-pink",
      "Pogreb": "bg-dark",
      "Božična": "bg-danger"
    };
    
    return categoryMap[category] || "bg-secondary";
  }, []);
  
  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = {
    songs,
    filteredSongs,
    loading,
    error,
    selectedOwner,
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    categories,
    availableTags,
    tagFilter,
    setTagFilter,
    sortField,
    sortDirection,
    showSongModal,
    setShowSongModal,
    currentSong,
    collections,
    selectedCollections,
    showCollectionModal,
    setShowCollectionModal,
    currentSongForCollection,
    handleSort,
    handleOwnerChange,
    handleSearch: setSearchTerm,
    handleOpenSongModal,
    handleOpenCollectionModal,
    handleCollectionSelect,
    handleAddToCollections,
    canEditSong,
    getCategoryBadgeClass,
    refreshSongs,
    handleDelete,
    getSongCountByOwnership
  };

  return (
    <SongContext.Provider value={contextValue}>
      {children}
    </SongContext.Provider>
  );
};

export default SongStateProvider;